import { use, useEffect } from 'react';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import useTableFiltering, { TableFiltering } from 'shared/components/table/hooks/table-filtering';
import SelectedOrganisationTreeNode from 'shared/features/organisation/models/selected-organisation-tree-node';

import ReadResidentsResponse from 'features/residents/models/read-residents-response';
import Resident from 'features/residents/models/resident';
import { FilterValueType } from 'features/residents/models/resident-settings-filter-value';
import useResidentSettingsNavigationState from 'features/residents/resident-settings/hooks/resident-settings-navigation-state';
import { useLazyReadResidents } from 'features/residents/resident-settings/providers/resident-queries';
import useResidentSettingDetailEditState, {
  ResidentDetailState,
} from 'features/residents/resident-settings/providers/states/resident-setting-detail-edit-state';
import useResidentSettingFiltersState, {
  ResidentSettingFiltersState,
} from 'features/residents/resident-settings/providers/states/resident-setting-filters.state';
import useResidentSettingsBulkEditState, {
  ResidentSettingsBulkEditState,
} from 'features/residents/resident-settings/providers/states/resident-settings-bulk-edit-state';

export type ResidentSettingsState = {
  residentSettings: ReadResidentsResponse | undefined;
  residentSettingsIsLoading: boolean;
  table: TableFiltering<Resident>;
  filtersState: ResidentSettingFiltersState;
  detailEditState: Omit<ResidentDetailState, 'closeDetails' | 'toggleDetailViewMode' | 'clearValidationErrorForScenario'> & {
    openDetails: (resident: Resident) => void;
  };
  bulkEditState: ResidentSettingsBulkEditState;
  refetchResidentSettings: () => void;
};

const useResidentSettingsState = (): ResidentSettingsState => {
  const { setErrorMessage } = use(ErrorMessageProviderContext);
  const { shouldPopUp, showDefaultConfirmationPopUp } = use(ConfirmationPopupProviderContext);

  const table = useTableFiltering<Resident>({
    defaultSort: {
      isAscending: true,
      property: 'organisationUnitName',
    },
    sessionStorageKey: 'resident-setting-sort',
  });

  const residentSettingsFiltersState = useResidentSettingFiltersState({ table });
  const residentSettingsQueryKey = JSON.stringify(residentSettingsFiltersState.getResidentSettingsQuery);

  const {
    refetch: getResidentSettings,
    data: residentSettings,
    isPending: residentSettingsIsLoading,
    error: residentsError,
    dataUpdatedAt: residentSettingsLastUpdatedAt,
  } = useLazyReadResidents(residentSettingsFiltersState.getResidentSettingsQuery);

  const residentSettingDetailEditState = useResidentSettingDetailEditState({
    residentSettings: residentSettings?.residents ?? [],
    residentSettingsLastUpdatedAt,
  });
  const { openDetailsInViewingMode } = residentSettingDetailEditState;
  const residentSettingsBulkEditState = useResidentSettingsBulkEditState();

  const openDetails = (resident: Resident): void => {
    if (residentSettingsBulkEditState.selectedResidentSettings.length > 0) {
      return;
    }
    showDefaultConfirmationPopUp({
      action: () => {
        residentSettingDetailEditState.toggleDetailViewMode(resident);
      },
      shouldShow: residentSettingDetailEditState.residentSettingEditForm.formState.isDirty,
    });
  };

  const searchBarConfirmationGuard = (changeLocalValue: () => void): void => {
    if (shouldPopUp) {
      showDefaultConfirmationPopUp({
        action: () => {
          residentSettingDetailEditState.disablePopUpGuard();
          changeLocalValue();
        },
      });
    } else {
      changeLocalValue();
    }
  };

  const selectOptions = (filterValueType: FilterValueType, keys: string[] | string): void => {
    residentSettingDetailEditState.closeDetails(() => residentSettingsFiltersState.selectOptions(filterValueType, keys as never));
  };

  const changeSelectedOrganizationUnits = (selectedOrganisationUnits: SelectedOrganisationTreeNode[]): void => {
    residentSettingDetailEditState.closeDetails(() =>
      residentSettingsFiltersState.changeSelectedOrganizationUnits(selectedOrganisationUnits),
    );
  };

  const refetchResidentSettings = (): void => {
    getResidentSettings();
  };

  const selectResidentSettings = (residents: Resident[]): void => {
    residentSettingDetailEditState.closeDetails();
    residentSettingsBulkEditState.selectResidentSettings(residents);
  };

  useEffect(() => {
    selectResidentSettings([]);
    refetchResidentSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [residentSettingsQueryKey]);

  useResidentSettingsNavigationState({
    residentSettings,
    residentSettingsQuery: residentSettingsFiltersState.getResidentSettingsQuery,
    applyNavigationFilters: residentSettingsFiltersState.applyNavigationFilters,
    clearSelectedResidentSettings: () => residentSettingsBulkEditState.selectResidentSettings([]),
    closeDetails: residentSettingDetailEditState.closeDetails,
    openDetailsInViewingMode,
  });

  useEffect(() => {
    if (!residentsError) return;
    setErrorMessage(residentsError);
  }, [residentsError, setErrorMessage]);

  return {
    residentSettings,
    residentSettingsIsLoading,
    table,
    refetchResidentSettings,
    filtersState: {
      ...residentSettingsFiltersState,
      searchBarConfirmationGuard,
      selectOptions,
      changeSelectedOrganizationUnits,
    },
    detailEditState: {
      ...residentSettingDetailEditState,
      openDetails,
    },
    bulkEditState: {
      ...residentSettingsBulkEditState,
      selectResidentSettings,
    },
  };
};

export default useResidentSettingsState;
